import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ApolloError, useApolloClient } from '@apollo/client';
import { GraphQLError } from 'graphql';

import {
  Toaster,
  useWishlistListSelectionDrawer,
} from '@notino/react-styleguide';
import { WishlistList } from '@notino/shared/definitions/types';

import {
  trackCreateNewList,
  TrackCreateNewListArgs,
} from '@containers/WishlistLists/tracking';
import { getWishlistTotalCount } from '@lib/wishlist/getWishlistTotalCount';

import messages from '../messages';
import { updateWishlistedCache } from '../updateWishlistedCache';
import { useWishlistLists } from '../useWishlistLists';
import { updateVariantWishlistedCache } from '../WishlistProductDetailIcon/updateVariantCache';

import {
  getWishlistErrorToastProps,
  getMultipleProductsNotificationMessage,
  getSingleProductNotificationMessage,
} from './utils';

type OriginalOnSaveParams = Parameters<
  Parameters<typeof useWishlistListSelectionDrawer>[0]['onSave']
>[0];

type AdditionalOnSaveParams = {
  listPosition?: number;
};

type Props = {
  onSave: (
    params: OriginalOnSaveParams &
      AdditionalOnSaveParams & {
        productIds: string[];
      }
  ) => Promise<void>;
  texts?: Partial<
    Parameters<typeof useWishlistListSelectionDrawer>[0]['texts']
  >;
  interactionSource?: TrackCreateNewListArgs['interaction'];
};

export const useWishlistSelectionDrawer = ({
  texts,
  onSave,
  interactionSource,
}: Props) => {
  const apollo = useApolloClient();
  const { formatMessage } = useIntl();
  const { createList } = useWishlistLists();
  const wishlistLists = React.useRef<WishlistList[] | null>(null);
  const productRef = React.useRef<
    AdditionalOnSaveParams & {
      productName: string;
      productId: string | string[];
    }
  >();

  const { open: _open, close } = useWishlistListSelectionDrawer({
    texts: {
      title: formatMessage(messages.selectListDrawerTitle),
      saveButton: formatMessage(messages.saveButton),
      newListButton: formatMessage(messages.newListButton),
      listNameLabel: formatMessage(messages.listNameLabel),
      createAndSaveButton: formatMessage(messages.createAndSaveButton),
      ...texts,
    },

    onSave: async ({ addedToLists, removedFromLists }) => {
      const currentProduct = productRef.current;
      if (!currentProduct) {
        return;
      }
      await onSave({
        addedToLists,
        removedFromLists,
        listPosition: currentProduct.listPosition,
        productIds: Array.isArray(currentProduct.productId)
          ? currentProduct.productId
          : [currentProduct.productId],
      });
      const productName = currentProduct.productName;

      const message = Array.isArray(currentProduct.productId)
        ? getMultipleProductsNotificationMessage({ addedToLists })
        : getSingleProductNotificationMessage({
            addedToLists,
            removedFromLists,
            productName,
          });

      Toaster.info({
        icon: 'IconRegularCheckmark',
        content: (
          <FormattedMessage
            {...message}
            values={{
              ...('values' in message ? message.values : {}),
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        ),
      });
    },

    onCreateNewList: async ({ newListName }) => {
      const currentProduct = productRef.current;
      trackCreateNewList({
        interaction: interactionSource!,
        wishlistTotalLists: (wishlistLists.current?.length ?? 0) + 1,
        wishlistTotalProducts: getWishlistTotalCount({
          lists: wishlistLists.current ?? [],
          addedToLists: [{ id: 'new-list-id' }], // just to count the new list
          webIds:
            currentProduct && Array.isArray(currentProduct.productId)
              ? currentProduct.productId.map(Number)
              : [Number(currentProduct?.productId)],
        }),
      });
      const productId: string =
        currentProduct && Array.isArray(currentProduct.productId)
          ? currentProduct.productId[0]
          : (currentProduct?.productId as string) ?? '';

      const newList = await createList({
        name: newListName,
        productIds: [productId],
      });

      updateWishlistedCache({
        cache: apollo.cache,
        products: [
          {
            productId,
            wishlisted: true,
          },
        ],
      });
      updateVariantWishlistedCache({
        cache: apollo.cache,
        productId,
        wishlisted: true,
      });

      return newList ? { ...newList, name: newList.name ?? '' } : null!;
    },

    getErrorToastProps: (error: ApolloError | GraphQLError) =>
      getWishlistErrorToastProps(error, wishlistLists.current ?? []),
  });

  const open = ({
    lists,
    productId,
    selectionMode,
    productName,
    listPosition,
  }: {
    lists: WishlistList[];
    selectionMode?: Parameters<typeof _open>[0]['selectionMode'];
    productId: string | string[];
    productName: string;
  } & AdditionalOnSaveParams) => {
    productRef.current = { productId, productName, listPosition };
    wishlistLists.current = lists;

    _open({
      lists: lists.map((list) => ({
        id: list.id,
        name: list.name ?? '',
        count: list.products.length,
        isSelected:
          typeof productId === 'string'
            ? list.products.includes(Number(productId))
            : false,
      })),
      selectionMode,
    });
  };

  return { open, close };
};
